import React, { useState, useRef, useEffect } from "react";
import { useGlobal } from "../../GlobalContext";
import { canList } from "./permissionUtils.js";

// The menu permission that governs every AI chat panel, module and global
// alike. Gating inside the component means one check covers all six mounts -
// a per-page check would be six places to forget.
const AI_CHAT_MODULE = "Ask MAI";

const PROVIDER_LABELS = { claude: "Claude", openai: "OpenAI", gemini: "Gemini" };

// The models answer in light markdown - bold, bullet lists, the odd code
// span - and a plain text node renders the asterisks literally. This covers
// that subset only, and builds React elements rather than HTML, so model
// output can never inject markup.
const inlineMarkdown = (text, key) => {
  const out = [];
  const pattern = /\*\*([^*]+)\*\*|`([^`]+)`/g;
  let last = 0;
  let n = 0;
  let m;

  while ((m = pattern.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(
      m[1] ? (
        <strong key={`${key}b${n++}`}>{m[1]}</strong>
      ) : (
        <code key={`${key}c${n++}`} style={mdCode}>
          {m[2]}
        </code>
      ),
    );
    last = pattern.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));

  return out;
};

const renderMarkdown = (text) => {
  const blocks = [];
  let items = null;

  const flushList = () => {
    if (items) {
      blocks.push(
        <ul key={`u${blocks.length}`} style={mdList}>
          {items}
        </ul>,
      );
      items = null;
    }
  };

  String(text)
    .split("\n")
    .forEach((raw, i) => {
      const line = raw.trim();
      const bullet = line.match(/^[*\-•]\s+(.*)$/);
      const numbered = line.match(/^\d+[.)]\s+(.*)$/);

      if (bullet || numbered) {
        items = items || [];
        items.push(
          <li key={`l${i}`} style={mdItem}>
            {inlineMarkdown((bullet || numbered)[1], `l${i}`)}
          </li>,
        );
        return;
      }

      flushList();
      if (line === "") return;

      blocks.push(
        <div key={`p${i}`} style={mdPara}>
          {inlineMarkdown(line, `p${i}`)}
        </div>,
      );
    });

  flushList();

  return blocks;
};

// Ask-your-data panel, shared by any module that exposes the ai_chat and
// ai_providers endpoints. History lives here and dies on navigate - reps
// rarely want yesterday's chat back, and keeping it in state means no table
// and no retention question about stored AI conversations.
const AiChat = ({ askApi, providersApi, title = "Ask MAI", hint = "" }) => {
  const { rolePermissionList } = useGlobal();
  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState([]);
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastUsage, setLastUsage] = useState(null);
  const [spent, setSpent] = useState(0);
  // Only providers the backend actually has a key for. Asked on open so the
  // picker never offers something that would fail.
  const [providers, setProviders] = useState([]);
  const [provider, setProvider] = useState("");
  const [model, setModel] = useState("");
  // The workbook arrives base64 on the reply, never through the model.
  const [download, setDownload] = useState(null);
  // A tool-using question is several round trips, so a wait of a few seconds
  // is normal. Counting it is the honest way to show the difference between
  // "working" and "stuck" - the server does not stream, so the client cannot
  // know which stage it is in, and inventing stage names would be a guess.
  const [elapsed, setElapsed] = useState(0);
  // One id per panel open, so the backend can group a conversation's rows and
  // sum its cost. Lazily initialised and never reset, because the transcript
  // is what makes the stored tokens interpretable.
  const sessionId = useRef(null);
  if (!sessionId.current) {
    sessionId.current =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
  const endRef = useRef(null);

  // The global "" sits higher than a module's own panel so the two
  // fixed buttons do not land on top of each other on a list page. Derived
  // once: the closed button and the open panel must agree or the panel jumps
  // when it opens.
  const bottomOffset = title === "Ask MAI" ? 64 : 128;

  const saveExport = () => {
    const bytes = Uint8Array.from(atob(download.base64), (c) =>
      c.charCodeAt(0),
    );
    const url = URL.createObjectURL(
      new Blob([bytes], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = download.filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const activeModels =
    providers.find((p) => p.provider === provider)?.models ?? [];

  // Switching provider must clear a model that belongs to the other one, or
  // the backend would silently fall back to that provider's default and the
  // picker would be lying about what ran.
  const chooseProvider = (next) => {
    const chosen = providers.find((p) => p.provider === next);
    setProvider(next);
    setModel(chosen?.model ?? chosen?.models?.[0] ?? "");
  };

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [turns, loading]);

  useEffect(() => {
    if (!loading) {
      setElapsed(0);
      return;
    }
    const started = Date.now();
    const id = setInterval(
      () => setElapsed(Math.floor((Date.now() - started) / 1000)),
      1000,
    );
    return () => clearInterval(id);
  }, [loading]);

  useEffect(() => {
    if (!open || providers.length) return;
    (async () => {
      const response = await providersApi();
      if (response.data?.success === true) {
        const list = response.data.providers ?? [];
        const initial = response.data.default || list[0]?.provider || "";
        const chosen = list.find((p) => p.provider === initial);
        setProviders(list);
        setProvider((prev) => prev || initial);
        // What the admin configured for the chatbot, then that provider's own
        // default, then whatever is first. Opening on models[0] ignored the
        // configured choice whenever it was not first in the list.
        setModel(
          (prev) =>
            prev ||
            response.data.default_model ||
            chosen?.model ||
            chosen?.models?.[0] ||
            "",
        );
      } else {
        // Hiding the picker on a failed fetch looks identical to "only one
        // provider configured", so say which it is.
        setError(
          response.data?.message ||
            "Could not load model options - is the backend up to date?",
        );
      }
    })();
  }, [open, providers.length]);

  const send = async () => {
    const asked = question.trim();
    if (!asked || loading) return;

    setQuestion("");
    setError("");
    setDownload(null);
    setTurns((prev) => [...prev, { role: "user", content: asked }]);
    setLoading(true);
    try {
      // The server caps and sanitises this, but trimming here keeps the
      // request small when a conversation runs long.
      const response = await askApi({
        message: asked,
        history: turns.slice(-10),
        provider,
        model,
        session_id: sessionId.current,
      });
      if (response.data?.success === true) {
        setTurns(response.data.history ?? []);
        const used = response.data.usage;
        if (used) {
          setLastUsage({ ...used, model: response.data.model });
          setSpent((prev) => prev + (used.cost_usd ?? 0));
        }
        setDownload(response.data.export ?? null);
      } else {
        setError(response.data?.message || "Could not answer that");
      }
    } finally {
      setLoading(false);
    }
  };

  // After the hooks, never before: returning early above them would change
  // the hook order between renders as permissions load.
/*   if (!canList(rolePermissionList, AI_CHAT_MODULE)) {
    return null;
  } */

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{ ...fab, bottom: bottomOffset, cursor: "pointer" }}
      >
        {title}
      </button>
    );
  }
  const panel = {
    position: "fixed",
    right: 24,
    bottom: bottomOffset,
    zIndex: 1050,
    width: 380,
    maxWidth: "calc(100vw - 48px)",
    height: 520,
    // Tracks the offset, or on a short viewport the raised panel pushes its
    // own header off the top of the screen.
    maxHeight: `calc(100vh - ${bottomOffset + 24}px)`,
    display: "flex",
    flexDirection: "column",
    background: "#FFFFFF",
    border: "1px solid #EBEBEB",
    borderRadius: 12,
    boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
  };

  return (
    <div style={panel}>
      {/* Inline styles cannot express keyframes, and the panel is meant to
          drop into any page without a stylesheet, so it carries its own. */}
      <style>{`
        @keyframes aiChatPulse {
          0%, 100% { transform: scale(.65); opacity: .45; }
          50%      { transform: scale(1);   opacity: 1; }
        }
        /* Sweeps a bright band across the label. background-clip:text is what
           makes the gradient show through the glyphs rather than behind them. */
        @keyframes aiChatShimmer {
          0%   { background-position: 180% 0; }
          100% { background-position: -80% 0; }
        }
        /* Motion here is decoration - the elapsed counter carries the actual
           information - so it goes away for anyone who asked for less of it. */
        @media (prefers-reduced-motion: reduce) {
          .ai-chat-orb, .ai-chat-thinking {
            animation: none !important;
          }
          .ai-chat-thinking {
            -webkit-text-fill-color: #5E6C84 !important;
            color: #5E6C84 !important;
          }
        }
      `}</style>

      <div style={header}>
        <span>{title}</span>
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {spent > 0 && (
            <span
              style={{ color: "#7A869A", fontSize: 11, fontWeight: 400 }}
              title="Estimated spend this session. Anthropic's console is authoritative."
            >
              ~${spent.toFixed(4)} this session
            </span>
          )}
          {/* <a href="/ai-usage" style={historyLink} title="Stored chat history, tokens and cost">
            History
          </a> */}
          <button type="button" onClick={() => setOpen(false)} style={closeBtn}>
            ×
          </button>
        </span>
      </div>

      <div style={body}>
        {turns.length === 0 && !loading && (
          <div style={{ color: "#7A869A", fontSize: 13 }}>{hint}</div>
        )}

        {turns.map((t, i) => (
          <div
            key={i}
            style={{
              ...bubble,
              alignSelf: t.role === "user" ? "flex-end" : "flex-start",
              background: t.role === "user" ? "#3D4AF5" : "#F4F5F7",
              color: t.role === "user" ? "#FFFFFF" : "#172B4D",
            }}
          >
            {t.role === "assistant" ? renderMarkdown(t.content) : t.content}
          </div>
        ))}

        {/* No bubble: the answer renders in this spot, so the loader sits
            where the text will appear rather than in a placeholder that then
            vanishes. */}
        {loading && (
          <div
            style={{
              alignSelf: "flex-start",
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "2px 2px 6px",
            }}
          >
            <span className="ai-chat-orb" style={orb} />
            <span className="ai-chat-thinking" style={thinking}>
              Thinking
            </span>
            {/* Only the count is claimed, never a stage - the server does not
                stream, so naming what it is "currently doing" would be a
                guess dressed up as progress. */}
            {elapsed >= 4 && (
              <span style={{ color: "#A9AFBC", fontSize: 11 }}>{elapsed}s</span>
            )}
          </div>
        )}

        {!loading && download && (
          <button type="button" onClick={saveExport} style={downloadBtn}>
            ⬇ {download.filename}
          </button>
        )}

        {!loading && lastUsage && (
          <div style={meter}>
            {lastUsage.model && `${lastUsage.model} · `}
            {lastUsage.cost_usd != null
              ? `~$${lastUsage.cost_usd.toFixed(4)}`
              : "cost n/a"}
            {" · "}
            {lastUsage.input.toLocaleString()} in /{" "}
            {lastUsage.output.toLocaleString()} out
            {lastUsage.cache_read > 0 &&
              ` · ${lastUsage.cache_read.toLocaleString()} cached`}
            {lastUsage.calls > 1 && ` · ${lastUsage.calls} calls`}
          </div>
        )}

        {error && (
          <div style={{ fontSize: 13 }}>{error}</div>
        )}

        <div ref={endRef} />
      </div>

      {(providers.length > 1 || activeModels.length > 1) && (
        <div style={switcher}>
          {providers.length > 1 &&
            providers.map((p) => (
              <button
                key={p.provider}
                type="button"
                onClick={() => chooseProvider(p.provider)}
                style={{
                  ...pill,
                  background:
                    provider === p.provider ? "#3D4AF5" : "transparent",
                  borderColor: provider === p.provider ? "#3D4AF5" : "#EBEBEB",
                  color: provider === p.provider ? "#FFFFFF" : "#7A869A",
                }}
              >
                {PROVIDER_LABELS[p.provider] ?? p.provider}
              </button>
            ))}

          {activeModels.length > 1 && (
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              style={modelSelect}
            >
              {activeModels.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      <div style={footer}>
        <input
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Ask a question…"
          maxLength={1000}
          style={input}
        />
        <button
          type="button"
          onClick={send}
          disabled={loading || !question.trim()}
          style={{
            ...sendBtn,
            opacity: loading || !question.trim() ? 0.5 : 1,
            cursor: loading || !question.trim() ? "default" : "pointer",
          }}
        >
          Send
        </button>
      </div>
    </div>
  );
};

const fab = {
  position: "fixed",
  right: 24,
  // Clear of the bottom-right action buttons the list pages put there.
  bottom: 24,
  zIndex: 1050,
  background: "#3D4AF5",
  border: "none",
  borderRadius: 24,
  color: "#FFFFFF",
  fontSize: 14,
  fontWeight: 500,
  padding: "12px 20px",
  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
};



const header = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "12px 16px",
  borderBottom: "1px solid #EBEBEB",
  color: "#172B4D",
  fontSize: 15,
  fontWeight: 600,
};

const historyLink = {
  color: "#7A869A",
  fontSize: 11,
  fontWeight: 500,
  textDecoration: "none",
};

const closeBtn = {
  background: "none",
  border: "none",
  color: "#7A869A",
  cursor: "pointer",
  fontSize: 22,
  lineHeight: 1,
  padding: 0,
};

const body = {
  flex: 1,
  overflowY: "auto",
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 10,
};

const bubble = {
  maxWidth: "85%",
  padding: "8px 12px",
  borderRadius: 12,
  fontSize: 13,
  lineHeight: 1.55,
  // Kept for the user's own text, which is not parsed. Assistant turns are
  // rendered as elements, which bring their own spacing.
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

const orb = {
  width: 8,
  height: 8,
  borderRadius: "50%",
  background: "#3D4AF5",
  display: "inline-block",
  flexShrink: 0,
  animation: "aiChatPulse 1.4s ease-in-out infinite",
};

const thinking = {
  fontSize: 13,
  fontWeight: 500,
  letterSpacing: 0.1,
  // The gradient is the visible text: clipped to the glyphs and swept across
  // them, so the word itself shimmers instead of sitting on a moving block.
  background: "linear-gradient(90deg,#B3B9C6 20%,#172B4D 45%,#172B4D 55%,#B3B9C6 80%)",
  backgroundSize: "220% 100%",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  WebkitTextFillColor: "transparent",
  color: "transparent",
  animation: "aiChatShimmer 1.9s linear infinite",
};

const mdPara = {
  margin: "0 0 6px",
};

const mdList = {
  margin: "0 0 6px",
  paddingLeft: 18,
};

const mdItem = {
  margin: "0 0 3px",
};

const mdCode = {
  background: "rgba(9,30,66,0.08)",
  borderRadius: 4,
  fontSize: 12,
  padding: "1px 4px",
};

const switcher = {
  alignItems: "center",
  borderTop: "1px solid #EBEBEB",
  color: "#7A869A",
  display: "flex",
  fontSize: 11,
  gap: 6,
  padding: "8px 12px 0",
};

const pill = {
  border: "1px solid #EBEBEB",
  borderRadius: 12,
  cursor: "pointer",
  fontSize: 11,
  fontWeight: 500,
  padding: "3px 10px",
};

const downloadBtn = {
  alignSelf: "flex-start",
  background: "#F4F5F7",
  border: "1px solid #3D4AF5",
  borderRadius: 12,
  color: "#3D4AF5",
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 500,
  maxWidth: "85%",
  overflow: "hidden",
  padding: "6px 12px",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const modelSelect = {
  border: "1px solid #EBEBEB",
  borderRadius: 12,
  color: "#7A869A",
  cursor: "pointer",
  fontSize: 11,
  maxWidth: 170,
  outline: "none",
  padding: "3px 6px",
};

const meter = {
  alignSelf: "flex-start",
  color: "#7A869A",
  fontSize: 11,
  paddingLeft: 4,
};

const footer = {
  display: "flex",
  gap: 8,
  padding: 12,
  borderTop: "1px solid #EBEBEB",
};

const input = {
  flex: 1,
  border: "1px solid #EBEBEB",
  borderRadius: 24,
  fontSize: 13,
  outline: "none",
  padding: "8px 14px",
};

const sendBtn = {
  background: "#3D4AF5",
  border: "none",
  borderRadius: 24,
  color: "#FFFFFF",
  fontSize: 13,
  fontWeight: 500,
  padding: "8px 18px",
};

export default AiChat;
